'use client'

import { useRef } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import {
  BeakerIcon,
  BoltIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  CpuChipIcon,
  HomeIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'

const SOLUTION_ITEMS = [
  {
    name: 'Water Softeners',
    description: 'Hard water, scale, spotting, and dry skin.',
    href: '/services/water-softener',
    icon: WrenchScrewdriverIcon,
  },
  {
    name: 'Drinking Water',
    description: 'Better taste and under-sink filtration options.',
    href: '/services/drinking-water',
    icon: BeakerIcon,
  },
  {
    name: 'UV Disinfection',
    description: 'Protection for bacteria and source-water risk.',
    href: '/services/uv-system',
    icon: BoltIcon,
  },
  {
    name: 'Business Solutions',
    description: 'Site-specific treatment for commercial needs.',
    href: '/services/business',
    icon: BuildingOfficeIcon,
  },
]

const TOOL_ITEMS = [
  {
    name: 'Check Water Hardness',
    description: 'Start with local hardness and water context.',
    href: '/water-hardness',
    icon: HomeIcon,
  },
  {
    name: 'Help Me Choose',
    description: 'Use a guided path when the right fit is unclear.',
    href: '/landings/help-me-choose',
    icon: ClipboardDocumentCheckIcon,
  },
]

function DropdownItem({ item, pathname, onClose, tool = false }) {
  const Icon = item.icon
  const isActive = pathname === item.href || pathname?.startsWith(`${item.href}/`)

  return (
    <Link
      href={item.href}
      onClick={onClose}
      className={`group flex min-w-0 gap-3 rounded-xl p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue ${
        isActive ? 'bg-water-aqua' : tool ? 'bg-water-canvas hover:bg-water-aqua/70' : 'hover:bg-water-canvas'
      }`}
    >
      <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg text-water-blue-dark transition-colors group-hover:bg-white ${tool ? 'bg-white' : 'bg-water-aqua'}`}>
        <Icon className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-water-ink">{item.name}</span>
        <span className="mt-0.5 block text-sm leading-5 text-water-muted">{item.description}</span>
      </span>
    </Link>
  )
}

export default function ServicesDropdown({
  isOpen,
  onToggle,
  onOpen,
  onLeave,
  onClose,
  triggerRef,
}) {
  const pathname = usePathname()
  const openedFromPointerRef = useRef(false)
  const isServiceActive = pathname?.startsWith('/services')
    || pathname === '/water-hardness'
    || pathname?.startsWith('/landings/help-me-choose')

  return (
    <div
      className="relative"
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          openedFromPointerRef.current = !isOpen
          onOpen()
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') {
          openedFromPointerRef.current = false
          onLeave()
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onClose()
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          if (event.detail > 0 && openedFromPointerRef.current) {
            openedFromPointerRef.current = false
            return
          }
          onToggle()
        }}
        className={`inline-flex min-h-10 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[15px] font-medium tracking-[-0.01em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 ${
          isOpen || isServiceActive
            ? 'bg-water-aqua text-water-blue-dark'
            : 'text-water-ink hover:bg-water-canvas hover:text-water-blue-dark'
        }`}
        aria-expanded={isOpen}
        aria-controls="desktop-solutions-menu"
        aria-haspopup="true"
      >
        Solutions
        <ChevronDownIcon
          className={`h-3.5 w-3.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div
          id="desktop-solutions-menu"
          className="absolute left-0 top-full z-50 w-[min(42rem,calc(100vw-2rem))] pt-3"
        >
          <div className="animate-nav-menu overflow-hidden rounded-2xl border border-water-border bg-white p-2 shadow-[0_16px_48px_rgba(11,34,57,0.10)]">
            <div className="px-3 pb-2 pt-2">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-water-muted">Water treatment solutions</p>
            </div>
            <div className="grid gap-1 md:grid-cols-2">
              {SOLUTION_ITEMS.map((item) => (
                <DropdownItem key={item.href} item={item} pathname={pathname} onClose={onClose} />
              ))}
            </div>
            <div className="mt-2 border-t border-water-border pt-2">
              <p className="px-3 pb-2 pt-1 text-xs font-semibold uppercase tracking-[0.08em] text-water-muted">Free decision tools</p>
              <div className="grid gap-1 md:grid-cols-2">
                {TOOL_ITEMS.map((item) => (
                  <DropdownItem key={item.href} item={item} pathname={pathname} onClose={onClose} tool />
                ))}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-water-ink px-4 py-3 text-sm">
              <Link
                href="/services"
                onClick={onClose}
                className="font-semibold text-white transition-colors hover:text-water-aqua focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                View All Solutions →
              </Link>
              <Link
                href="/services/plc-industrial-programming"
                onClick={onClose}
                className="inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <CpuChipIcon className="h-4 w-4" aria-hidden="true" />
                Industrial PLC
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
